import { ClickAwayListener } from '@mui/base';
import { Box, useTheme } from '@mui/joy';
import { KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

export interface IProps {
  value: string;
  onChange: (hex: string) => void;
}

const POPOVER_Z_INDEX = 20;
// The hex box reports shorthand like "#1A7" while it is typed; only whole colours are passed on.
const FULL_HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/i;
// Picker (200px) plus the popover's own horizontal padding (12px each side).
const POPOVER_WIDTH = 224;

// A swatch that opens a saturation/hue picker with its own hex input - the native
// `input[type=color]` opens OS chrome that has no hex field.
export const ColorPickerField = ({ value, onChange }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      return;
    }

    // Near the right edge (as in a drawer) a left-aligned popover would overflow, so flip it.
    const { left } = containerRef.current.getBoundingClientRect();

    setAlignRight(left + POPOVER_WIDTH > window.innerWidth);
  }, [isOpen]);

  const onTogglePicker = () => setIsOpen((prev) => !prev);

  const onHexInputChange = (hex: string) => {
    if (FULL_HEX_COLOR_PATTERN.test(hex)) {
      onChange(hex);
    }
  };

  const onClickAway = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  // Escape closes the picker without reaching the drawer around it, which would close too.
  const onKeyDown = (event: KeyboardEvent) => {
    if ('Escape' !== event.key || !isOpen) {
      return;
    }

    event.stopPropagation();
    setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box ref={containerRef} onKeyDown={onKeyDown} sx={{ position: 'relative' }}>
        <Box
          component="button"
          type="button"
          aria-label={t('labelPickColor')}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={onTogglePicker}
          sx={{
            display: 'block',
            padding: 0,
            width: 32,
            height: 32,
            borderRadius: 6,
            border: '1px solid',
            borderColor: 'neutral.300',
            backgroundColor: value,
            cursor: 'pointer',
          }}
        />

        {isOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              ...(alignRight ? { right: 0 } : { left: 0 }),
              zIndex: POPOVER_Z_INDEX,
              backgroundColor: 'background.surface',
              border: '1px solid',
              borderColor: 'neutral.300',
              borderRadius: 6,
              boxShadow: 'md',
              padding: 1.5,
            }}
          >
            <HexColorPicker color={value} onChange={onChange} />

            {/* A third-party DOM input takes a style object, so the theme values are read here. */}
            <HexColorInput
              color={value}
              onChange={onHexInputChange}
              prefixed
              style={{
                display: 'block',
                width: 200,
                height: 32,
                marginTop: 8,
                padding: '0 12px',
                borderRadius: 6,
                border: `1px solid ${theme.palette.neutral[300]}`,
                fontSize: theme.fontSize.sm,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};
